import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { LoadingContainer, useErrorSnackbar } from '@pflegenavi/web-components';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import {
  useRefetchServiceProviderStripeAccount,
  useServiceProviderStripeAccount,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

export const ServiceProviderStripeOnboardingComplete: FC = () => {
  // The user has completed the onboarding process and is now redirected to this page.
  // The backend will receive an event from Stripe and will update the account.
  // However, this might be delayed, so we'll trigger a refresh of the account here.
  // @ts-expect-error // getTokenImmediate is intentionally hidden.
  const { getTokenImmediate } = useAuthentication();
  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );

  const { data: stripeAccount } = useServiceProviderStripeAccount({
    globalServiceProviderId,
  });

  const navigate = useNavigate();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { t } = useTranslation();

  const { mutateAsync: refetchServiceProviderStripeAccount } =
    useRefetchServiceProviderStripeAccount();

  const refetchAndNavigate = useCallback(async () => {
    if (!globalServiceProviderId) {
      return;
    }

    try {
      await refetchServiceProviderStripeAccount({
        globalServiceProviderId,
      });
    } catch (error) {
      enqueueErrorSnackbar(error, t('errors.something-went-wrong'));
    }
    navigate(spAppMainPages.PROFILE);
  }, [
    enqueueErrorSnackbar,
    globalServiceProviderId,
    navigate,
    refetchServiceProviderStripeAccount,
    t,
  ]);

  useEffect(() => {
    if (
      stripeAccount?.stripeAccountId &&
      stripeAccount.accountOnboardingStatus === 'pending'
    ) {
      // The account is still pending, so we need to refetch it.
      refetchAndNavigate().catch(Sentry.captureException);
    } else if (
      stripeAccount?.stripeAccountId &&
      stripeAccount.accountOnboardingStatus === 'complete'
    ) {
      // Already active, so we can redirect to the profile page.
      navigate(spAppMainPages.PROFILE);
    }
  }, [navigate, refetchAndNavigate, stripeAccount]);

  return <LoadingContainer />;
};

export default ServiceProviderStripeOnboardingComplete;
